import { zodResolver } from "@hookform/resolvers/zod";
import {
  PaymentElement,
  useElements,
  useStripe,
} from "@stripe/react-stripe-js";
import { type StripeError } from "@stripe/stripe-js";
import { IconCheck } from "@tabler/icons-react";
import { observer } from "mobx-react-lite";
import * as React from "react";
import { type PropsWithChildren } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import {
  Button,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
  Form,
  twx,
  cn,
} from "ui";
import { z } from "zod";

import { StripeElements } from "src/components/StripeElements";
import { stripePriceFormatted } from "src/helpers/payments";
import { trpcClient } from "src/trpc";

const Checkmark = twx(IconCheck)`h-6 w-6 text-emerald-500`;

function Feature(props: PropsWithChildren) {
  return (
    <li className="flex">
      <Checkmark />
      <span className="ml-3">{props.children}</span>
    </li>
  );
}

interface CardProps {
  onPromoCodeClick: () => void;
  onPaymentFormClick: () => void;
}

const FormWrapper = twx("div")`mx-auto max-w-3xl`;

const promoCodeFormSchema = z.object({
  code: z.string(),
});

type PromoCodeFormSchema = z.infer<typeof promoCodeFormSchema>;

interface PromoCodeFormProps {
  onCancelClick: () => void;
}

function PromoCodeForm(props: PromoCodeFormProps) {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const form = useForm<PromoCodeFormSchema>({
    resolver: zodResolver(promoCodeFormSchema),
    defaultValues: {
      code: "",
    },
  });

  async function submitInviteCode(data: PromoCodeFormSchema) {
    await trpcClient.useInvitationCode.mutate({ inviteCode: data.code });
    navigate(".", { replace: true });
  }

  return (
    <FormWrapper>
      <Form {...form}>
        <form
          className="space-y-4 md:space-y-6"
          action="#"
          onSubmit={form.handleSubmit(submitInviteCode)}
        >
          <FormField
            control={form.control}
            name="code"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="inline-flex items-center space-x-1">
                  <div>{t("promoCodeForm.fields.promoCode.label")}</div>
                  <FormDescription className="text-red-500">*</FormDescription>
                </FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    autoFocus
                    placeholder={t(
                      "promoCodeForm.fields.promoCode.placeholder",
                    )}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <div className="flex justify-between">
            <Button
              type="button"
              variant="outline"
              onClick={props.onCancelClick}
            >
              {t("promoCodeForm.cancel")}
            </Button>
            <Button type="submit">{t("promoCodeForm.submit")}</Button>
          </div>
        </form>
      </Form>
    </FormWrapper>
  );
}

interface PaymentFormProps {
  onCancelClick: () => void;
}

function PaymentForm(props: PaymentFormProps) {
  return (
    <FormWrapper>
      <StripeElements fallback={null}>
        <PaymentFormLoaded onCancelClick={props.onCancelClick} />
      </StripeElements>
    </FormWrapper>
  );
}

interface PaymentFormLoadedProps {
  onCancelClick: () => void;
}

const paymentBoxWrapper =
  "border border-gray-200 p-4 shadow-sm rounded-xl min-h-[360px]";

function PaymentFormLoaded(props: PaymentFormLoadedProps) {
  const [isStripeLoading, setIsStripeLoading] = React.useState(true);
  const stripe = useStripe();
  const elements = useElements();
  const { t } = useTranslation();

  const [errorMessage, setErrorMessage] = React.useState("");
  const [loading, setLoading] = React.useState(false);

  const handleError = (error: Error | StripeError) => {
    setLoading(false);
    setErrorMessage(error?.message || "Unknown error");
  };

  async function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    setLoading(true);

    // Trigger form validation and wallet collection
    const { error: submitError } = await elements.submit();
    if (submitError) {
      handleError(submitError);
      return;
    }

    const { type, clientSecret } = await trpcClient.createSubscription.mutate();

    const confirmIntent =
      type === "setup" ? stripe.confirmSetup : stripe.confirmPayment;

    const data = await confirmIntent({
      elements,
      clientSecret,
      confirmParams: {
        return_url: `${window.location.origin}/payment-success`,
      },
      redirect: "if_required",
    });

    if (data.error) {
      handleError(data.error);
    } else if (
      "paymentIntent" in data &&
      data?.paymentIntent?.status === "succeeded"
    ) {
      await trpcClient.successfulSubscriptionPayment.mutate({
        paymentIntentId: data.paymentIntent.id,
      });
      window.location.reload();
    } else {
      setErrorMessage("Unexpected state");
    }

    setLoading(false);
  }

  return (
    <div className="flex space-x-4">
      <form
        id="payment-form"
        onSubmit={handleSubmit}
        className={cn(paymentBoxWrapper, "flex-1")}
      >
        <div className="font-semibold text-2xl pb-4">
          {t("paymentForm.addMethod")}
        </div>
        <PaymentElement onReady={() => setIsStripeLoading(false)} />
        {errorMessage && (
          <div className="flex justify-center text-red-500 mt-4">
            {errorMessage}
          </div>
        )}
      </form>
      <div className={cn(paymentBoxWrapper, "flex flex-col w-[320px]")}>
        <div className="font-semibold text-2xl">{t("paymentForm.summary")}</div>
        <div className="grow flex flex-col justify-between">
          <div className="pt-4 flex justify-between items-center">
            <div className="pt-4">Pro Plan</div>
            <div>
              <strong>{stripePriceFormatted}</strong>
              <span className="text-gray-500"> / {t("month")}</span>
            </div>
          </div>
          <div className="flex justify-between items-center">
            <span>{t("paymentForm.due")}:</span>{" "}
            <span className="font-semibold text-2xl">
              {stripePriceFormatted}
            </span>
          </div>
        </div>
        <div>
          <div>
            <Button
              className="mt-2 w-full"
              loading={loading}
              disabled={isStripeLoading || loading}
              form="payment-form"
            >
              {t("paymentForm.submit")}
            </Button>
          </div>
          <div>
            <Button
              className="mt-2 w-full"
              variant="outline"
              onClick={props.onCancelClick}
              disabled={loading}
            >
              {t("paymentForm.cancel")}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

function Card(props: CardProps) {
  const { t } = useTranslation();

  return (
    <div className="space-y-6">
      <div className="flex justify-center space-y-12 lg:space-y-0 p-2">
        <div className="relative lg:w-[440px] w-full p-6 border border-gray-200 rounded-2xl shadow-sm flex flex-col">
          <p className="absolute top-0 py-1.5 px-4 bg-emerald-500 text-white rounded-full text-xs font-semibold uppercase tracking-wide transform -translate-y-1/2">
            {t("paywall.most_popular")}
          </p>
          <div className="flex-1 space-y-4">
            <h3 className="text-2xl font-semibold">Pro</h3>
            <p className="flex items-baseline ">
              <span className="text-3xl font-extrabold tracking-tight">
                {stripePriceFormatted}
              </span>
              <span className="ml-1 text-xl font-semibold">
                / {t("paywall.monthlyPlan.unit")}
              </span>
            </p>
            <p>
              <span className="text-xl font-semibold">
                {t("paywall.whats_included")}
              </span>
            </p>
            <ul role="list" className="space-y-2">
              {t("paywall.features", { returnObjects: true }).map((feature) => (
                <Feature key={feature}>{feature}</Feature>
              ))}
            </ul>
          </div>
          <div className="space-y-2">
            <div>
              <Button
                className="bg-emerald-500 hover:bg-emerald-600 mt-6 w-full"
                onClick={props.onPaymentFormClick}
              >
                {t("paywall.upgrade")}
              </Button>
            </div>
            <div className="text-center">
              <span className="mt-1 text-xs text-zinc-500">{t("or")}</span>
            </div>
            <div>
              <Button
                className="bg-zinc-500 hover:bg-zinc-600 mt-1 w-full"
                onClick={props.onPromoCodeClick}
              >
                {t("paywall.promoCode")}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export const UpgradePaywall = observer(function UpgradePaywall() {
  const { t } = useTranslation();
  const [view, setView] = React.useState<"promo-code" | "payment-form" | null>(
    null,
  );

  return (
    <div className="h-full relative z-10 flex flex-col pt-[var(--sztama-auth-toolbar-height)]">
      <main className="grow">
        <div className="mb-6">
          <h2 className="text-lg font-bold tracki text-center mt-4 sm:text-5xl ">
            {t("paywall.title")}
          </h2>
          <p className="max-w-3xl mx-auto mt-4 text-md text-center ">
            {t("paywall.subtitle")}
          </p>
        </div>

        {view === "promo-code" && (
          <div className="w-[420px] m-auto">
            <PromoCodeForm
              onCancelClick={() => {
                setView(null);
              }}
            />
          </div>
        )}
        {view === "payment-form" && (
          <PaymentForm
            onCancelClick={() => {
              setView(null);
            }}
          />
        )}
        {view === null && (
          <Card
            onPromoCodeClick={() => {
              setView("promo-code");
            }}
            onPaymentFormClick={() => {
              setView("payment-form");
            }}
          />
        )}
      </main>
    </div>
  );
});
